import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { AbstractDetailsComponent } from '../abstract-details.component';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Subscription, Observable, defer } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { React } from '../bb-react/bb-react.component';
import { PublicationService } from 'app/services/publication.service';
import { ClipboardService } from 'ngx-clipboard';
import { BbSelectOption } from '../bb-select/bb-select.component';

@Component({
	selector: 'bb-react-quickedit',
	templateUrl: './bb-react-quickedit.component.html',
	styleUrls: ['./bb-react-quickedit.component.scss']
})

export class BbReactQuickEditComponent extends AbstractDetailsComponent implements OnInit, OnDestroy {

	@Output() saved = new EventEmitter<React>();
	@Output() removed = new EventEmitter<any>();

	@Input() projectContextOptions: BbSelectOption[];
	@Input() parentForm: UntypedFormGroup;
	@Input() get row() {
		return this._row;
	}
	set row(value) {
		this._row = value;
	}
	_row: any;

	saveAction: Observable<any>;
	entity: React;
	reactUrl: string;

	formGroup = new UntypedFormGroup({});
	formGroupName: string;
	subscriptions: Subscription = new Subscription();

	constructor(
		protected route: ActivatedRoute,
		protected location: Location,
		private publicationService: PublicationService,
		private clipboard: ClipboardService
	) {
		super(route, location);

		this.entityConfiguration = {
			name: 'react',
			entityTranslation: this.translate.instant('REACT'),
			standAlone: true, // get by react ID + parentPath and basePath are not used therefor
			parentPath: '/library',
			basePath: '/library/'
		};
	}

	ngOnInit() {
		if (this.row && this.row.data) {
			this.entity = this.row.data;
		}
		this.reactUrl = `https://${this.publicationService.baseUrl}/react/#/?hash=${this.entity.hash}`;

		this.prefillForm();
		super.ngOnInit();

		// values changed: update entity
		this.subscriptions.add(
			this.formGroup.valueChanges.pipe(
				debounceTime(200)
			).subscribe((fields: any) => {
				// After first update: save action to action bar
				this.setSaveListener();
				const fieldsUpdate = {};
				Object.keys(fields).forEach((field: string) => {
					fieldsUpdate[field] = fields[field];
				});
				this._row.data = { ...this._row.data, ...fieldsUpdate };
				this.entity = this._row.data;
			})
		);
	}

	ngOnDestroy() {
		if (this.parentForm) {
			this.parentForm.removeControl(this.formGroupName);
		}

		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	saveQuickEdit() {
		this.save(true);
	}

	onResetPassword(): void {
		this.formGroup.patchValue({
			password: '',
		});
		this.entity.password_set = false;
	}

	valueChanged(field: 'playoutname' | 'context' | 'notifications' | 'timelineversion', value: any): void {
		this.formGroup.patchValue({[field]: value});
	}

	save(force: boolean): Observable<any> {
		// to keep the same (already hashed) password, notify backend with keep_password
		if (this.entity.password_set) {
			this.entity.keep_password = true;
		}

		const saveSubject = super.save(force, null, false);

		saveSubject.subscribe((response) => {
			this.row.quickEditing = false;
			this.resetEntity();
		});
		return saveSubject;
	}

	removeSaveAction() {
		if (this.saveAction) {
			this.quickeditService.removeSaveAction(this.saveAction);
		}
	}

	delete(): any {
		this.removed.emit();
	}

	resetEntity() {
		this.removeSaveAction();
		super.cancel().subscribe((entity) => {
			this.row.data = entity;
			this.entity = entity as React;
		});
	}

	onCopyUrl(): void {
		this.clipboard.copyFromContent(this.reactUrl);
		this.overlayService.createToast(this.translate.instant('REACT_CODE_COPIED_TO_CLIPBOARD'), 'copy');
	}

	onCancelClick() {
		this.resetEntity();
		this.row.quickEditing = false;
	}

	private prefillForm(): void {
		const fields: string[] = ['label', 'playoutname', 'timelineversion', 'notifications', 'password'];
		this.formGroupName = 'react-quickedit-' + new Date().getTime();

		this.formGroup.addControl('playoutname', new UntypedFormControl('default', { validators: Validators.required }));
		if (this.projectContextOptions) {
			fields.splice(2, 0, 'context');
			this.formGroup.addControl('context', new UntypedFormControl(null));
		}
		this.formGroup.addControl('timelineversion', new UntypedFormControl('published'));
		this.formGroup.addControl('label', new UntypedFormControl(this.translate.instant('NEW_REACT_PAGE'), { validators: Validators.required }));
		this.formGroup.addControl('notifications', new UntypedFormControl(true));
		this.formGroup.addControl('password', new UntypedFormControl(''));

		if (this.parentForm) {
			this.parentForm.addControl(this.formGroupName, this.formGroup);
		}

		if (this.entity) {
			fields.forEach((fieldName: string) => {
				const formControl: UntypedFormControl = this.formGroup.get(fieldName) as UntypedFormControl;
				if (this.entity[fieldName]) {
					formControl.setValue(this.entity[fieldName]);
				} else if (fieldName === 'notifications') {
					// existing (old) React items -before this prop existed- should default to false for notifications
					formControl.setValue(false);
				}  else if (fieldName === 'timelineversion') {
					// existing (old) React items -before this prop existed- should default to published for timelineversion
					this.entity[fieldName] = 'published';
					formControl.setValue('published');
				}
			});
		}
	}

	// when saving parent: also save all changed react quick edits
	private setSaveListener(): void {
		if (this.saveAction) {
			return; // only set once
		}
		this.saveAction = defer(() => this.save(true));
		this.quickeditService.addSaveAction(this.saveAction);
	}
}
